import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import PremiumServicesModal from '../components/PremiumServicesModal';
import { Shield, Search, Package, Check, ArrowRight, Sparkles, Home, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const iconMap = {
  Shield: Shield,
  Search: Search,
  Package: Package
};

const PremiumPage = () => {
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Get domain from URL if provided
  const domainFromUrl = searchParams.get('domain') || '';
  
  // Load services from API
  useEffect(() => {
    const loadServices = async () => {
      try {
        const res = await fetch(`${API_URL}/api/services`);
        const data = await res.json();
        setServices(data.services || {});
      } catch (err) {
        console.error('Error loading services:', err);
      }
      setLoading(false);
    };
    loadServices();
  }, []);

  // Auto-open modal if service param is provided
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    if (serviceParam && services[serviceParam]) {
      setSelectedService(serviceParam);
      setShowModal(true);
    }
  }, [searchParams, services]);

  const handleSelectService = (serviceId) => {
    setSelectedService(serviceId);
    setShowModal(true);
  };

  // Convert services object to sorted array
  const servicesList = Object.values(services).sort((a, b) => (a.order || 99) - (b.order || 99));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <Header showGuide={false} />
      
      <main className="pt-40 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full mb-6">
              <Sparkles className="h-4 w-4 text-red-400" />
              <span className="text-red-400 text-sm font-medium">Servizi Premium</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Proteggi la tua <span className="text-red-500">Azienda</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Scopri le vulnerabilità nascoste e proteggi i tuoi asset digitali con i nostri servizi di sicurezza avanzata
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {servicesList.map((service) => {
              const Icon = iconMap[service.icon] || Shield;
              return (
                <div
                  key={service.id}
                  className={`relative bg-[#1a1a2e] rounded-2xl border border-gray-800 overflow-hidden transition-all hover:scale-[1.02] hover:shadow-xl`}
                >
                  {service.is_bundle && service.savings && (
                    <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-xs font-bold text-white">
                      RISPARMIA €{service.savings}
                    </div>
                  )}
                  
                  <div className="p-6">
                    {/* Icon */}
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${service.color || 'from-gray-500 to-gray-600'} mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-bold text-white mb-2">{service.name}</h3>
                    <p className="text-gray-400 text-sm mb-4">{service.description}</p>
                    
                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {(service.features || []).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                          <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Price */}
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-white">€{service.price_gross}</div>
                      <div className="text-sm text-gray-500">€{service.price_net} + IVA</div>
                    </div>
                    
                    {/* CTA Button */}
                    <button
                      onClick={() => handleSelectService(service.id)}
                      className={`w-full py-3 px-4 rounded-xl bg-gradient-to-r ${service.color || 'from-gray-500 to-gray-600'} text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-all`}
                    >
                      Acquista Ora <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Back to Home */}
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all"
            >
              <Home className="h-4 w-4" />
              Torna alla Home
            </Link>
          </div>

          {/* Trust indicators */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="text-3xl font-bold text-red-500 mb-2">500+</div>
              <div className="text-gray-400">Aziende protette</div>
            </div>
            <div className="p-6">
              <div className="text-3xl font-bold text-red-500 mb-2">24h</div>
              <div className="text-gray-400">Consegna report</div>
            </div>
            <div className="p-6">
              <div className="text-3xl font-bold text-red-500 mb-2">100%</div>
              <div className="text-gray-400">Soddisfazione garantita</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-gray-600 text-sm border-t border-gray-800">
        © {new Date().getFullYear()} Smart Info Cyber Security - Tutti i diritti riservati
      </footer>

      {/* Premium Services Modal */}
      <PremiumServicesModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        analyzedDomain={domainFromUrl}
        preSelectedService={selectedService}
      />
    </div>
  );
};

export default PremiumPage;
